<template>
    <LoadingModal
        v-if="loading"
        @close="$emit('abort')"
    />
    <EditEmailTemplateModal
        v-else-if="initialValue"
        open
        :initial-value="initialValue"
        :submitting="submitting"
        :custom-properties="customProperties"
        @close="$emit('abort')"
        @submit="onSubmit"
    />
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';
    import {
        NotificationConfiguration,
        NotificationType,
    } from '@evidentid/tprm-portal-lib/models/notification-configuration';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import LoadingModal from '@/components/loading-modal/LoadingModal.vue';
    import { useNotificationsConfigurationStore } from '@/modules/notifications-configuration/store';

    import EditEmailTemplateModal from '../components/EditEmailTemplateModal.vue';
    import { NotificationsConfigurationState } from '../types';

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires. This is the family observed live during MODE-3 QA at
    // #/aleksandrpf/notifications/email-settings (edit template pencil -> modal).
    @Component({
        components: {
            LoadingModal,
            EditEmailTemplateModal,
        },
        emits: [ 'abort', 'finish' ],
    })
    export default class EditEmailTemplateProcedure extends Vue {
        private get notificationsConfigurationStore() {
            return useNotificationsConfigurationStore();
        }

        // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't warn
        // "Extraneous non-props attributes (uuid)" (see UploadEntityDocumentProcedure.vue).
        @Prop({ type: String, default: '' })
        private uuid?: string;

        @Prop({ type: String })
        private rpName!: string;

        @Prop({ type: String })
        private type!: NotificationType;

        private get state(): NotificationsConfigurationState {
            return this.notificationsConfigurationStore.$state;
        }

        private get initialValue(): NotificationConfiguration | null {
            return this.state.emailTemplates?.[this.rpName]?.[this.type] || null;
        }

        private get customProperties(): string[] {
            return this.state.insuredFields?.[this.rpName] || [];
        }

        private get loadStatus(): OperationStatus {
            return this.state.loadEmailTemplatesStatus?.[this.rpName] || OperationStatus.uninitialized;
        }

        private get loadCustomPropertiesStatus(): OperationStatus {
            return this.state.loadInsuredFields?.[this.rpName] || OperationStatus.uninitialized;
        }

        private get updateStatus(): OperationStatus {
            return this.state.updateEmailTemplates?.[this.rpName]?.[this.type] || OperationStatus.uninitialized;
        }

        @Watch('loadFailed', { immediate: true })
        private onLoadFailed(): void {
            if (this.loadFailed) {
                this.$emit('abort');
            }
        }

        private get loadFailed(): boolean {
            return this.loadStatus === OperationStatus.error
                || this.loadCustomPropertiesStatus === OperationStatus.error;
        }

        private get loading(): boolean {
            return (
                [ OperationStatus.uninitialized, OperationStatus.loading ].includes(this.loadStatus) ||
                [ OperationStatus.uninitialized, OperationStatus.loading ].includes(this.loadCustomPropertiesStatus)
            );
        }

        private get submitting(): boolean {
            return this.updateStatus === OperationStatus.loading;
        }

        private async onSubmit(template: NotificationConfiguration): Promise<void> {
            await this.notificationsConfigurationStore.updateEmailTemplate({
                rpName: this.rpName,
                type: this.type,
                template,
            });

            if (this.updateStatus === OperationStatus.success) {
                await useSnackbarStore().displaySnackbar({
                    message: 'Email template has been updated successfully.',
                    success: true,
                });
                this.$emit('finish');
            }
        }

        private mounted(): void {
            this.notificationsConfigurationStore.loadEmailTemplates({ rpName: this.rpName });
            if ([ OperationStatus.uninitialized, OperationStatus.error ].includes(this.loadCustomPropertiesStatus)) {
                this.notificationsConfigurationStore.loadInsuredFieldIds({ rpName: this.rpName });
            }
        }
    }
</script>
